import { faChartPie } from "@fortawesome/free-solid-svg-icons";
import { percent, px, rem } from "csx";
import { Horizontal, Vertical } from "gls/lib";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { DashboardsContext } from "@/shared/context/Dashboards";
import { style } from "typestyle";
import { useContextSelector } from "use-context-selector";
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
} from "../accordion/Accordion";
import { defaultSearchParams } from "./Sidebar";
import { SidebarItem } from "./SidebarItem";

interface DashboardsProps {
  isCollapsed: boolean;
}

export const Dashboards: FC<DashboardsProps> = ({ isCollapsed }) => {
  const { t } = useTranslation();

  const dashboards = useContextSelector(
    DashboardsContext,
    (context) => context.dashboards,
  );

  const sortedDashboards =
    [...(dashboards || [])]?.sort((a, b) => (b.order < a.order ? 1 : -1)) || [];

  const mainDashboard = sortedDashboards[0];
  const subDashboards = dashboards?.filter((_, i) => i !== 0) || [];

  if (!dashboards?.length) return null;

  return (
    <Vertical>
      {isCollapsed ? (
        mainDashboard && (
          <SidebarItem
            isCollapsed={isCollapsed}
            icon={faChartPie}
            text={mainDashboard.name}
            collapsedText={"Board"}
            navigateTo={`/tide/dashboard/${mainDashboard.id}`}
            navigateSearch={defaultSearchParams}
            permissionName={"DASHBOARD_PAGE"}
            data-testid="sidebar-item-dashboard"
          />
        )
      ) : (
        <Accordion>
          {mainDashboard && (
            <>
              {subDashboards.length ? (
                <AccordionHeader id={1}>
                  <SidebarItem
                    isCollapsed={isCollapsed}
                    icon={faChartPie}
                    text={mainDashboard.name}
                    collapsedText={"Board"}
                    navigateTo={`/tide/dashboard/${mainDashboard.id}`}
                    navigateSearch={defaultSearchParams}
                    permissionName={"DASHBOARD_PAGE"}
                    data-testid="sidebar-item-dashboard"
                  />
                </AccordionHeader>
              ) : (
                <SidebarItem
                  isCollapsed={isCollapsed}
                  icon={faChartPie}
                  text={t("Overview")}
                  collapsedText={"Board"}
                  navigateTo={`/tide/dashboard/${mainDashboard.id}`}
                  navigateSearch={defaultSearchParams}
                  permissionName={"DASHBOARD_PAGE"}
                  data-testid="sidebar-item-dashboard"
                />
              )}

              <SubSidebarItems
                dashboards={subDashboards}
                isCollapsed={isCollapsed}
              />
            </>
          )}
        </Accordion>
      )}
    </Vertical>
  );
};

const SubSidebarItems: FC<{
  isCollapsed: boolean;
  dashboards: {
    id: number;
    isMain?: boolean;
    name: string;
  }[];
}> = ({ dashboards, isCollapsed }) => {
  if (!dashboards.length) return null;

  return (
    <AccordionContent id={1}>
      <Vertical
        width={percent(100)}
        spacing={rem(1)}
        height={percent(100)}
        padding={[0, 0, 0, px(20)]}
        className={style({
          boxSizing: "border-box",
        })}
      >
        {dashboards
          .filter((d) => !d.isMain)
          .map((d) => (
            <Horizontal key={d.id}>
              <SidebarItem
                isCollapsed={isCollapsed}
                text={d.name}
                navigateTo={`/tide/dashboard/${d.id}`}
                navigateSearch={defaultSearchParams}
              />
            </Horizontal>
          ))}
      </Vertical>
    </AccordionContent>
  );
};
